<template>
  <div id="PaymentCode" class="wrap">
    <van-nav-bar
      z-index="10"
      fixed
      placeholder
      :title="$t('My.PersonalInformation.Page.TransactionPassword')"
      left-arrow
      @click-left="onClickLeft"
    />
    <div class="payment-password-content">
      <van-form
        ref="passwordForm"
        :disabled="busy"
        validate-first
        validate-trigger="onSubmit"
        @submit="submit"
      >
        <div class="password-fields">
          <van-field
            v-if="requiresOldPassword"
            v-model="formData.oldpass"
            name="oldpass"
            :label="$t('password.old')"
            :placeholder="$t('password.old.placeholder')"
            :rules="formRules.oldpass"
          />
          <van-field
            v-model="formData.newpass"
            name="newpass"
            :label="$t('password.new')"
            :placeholder="$t('password.new.placeholder')"
            :rules="formRules.newpass"
          />
          <van-field
            v-model="formData.confirmpass"
            name="confirmpass"
            :label="$t('password.confirmPassword')"
            :placeholder="$t('password.confirmPassword.placeholder')"
            :rules="formRules.confirmpass"
          />
        </div>
        <div class="button-confirm">
          <van-button
            class="payment-password-submit"
            block
            native-type="submit"
            :loading="busy"
            :disabled="busy"
          >
            {{ $t('My.PersonalInformation.Password.Button') }}
          </van-button>
        </div>
      </van-form>
    </div>
  </div>
</template>

<script>
import { SETTING_PAY_PDW, userinfo } from "@/api";
import { Button, Field, Form, NavBar, Toast } from "vant";

export default {
  name: "PaymentCode",
  components: {
    [Button.name]: Button,
    [Field.name]: Field,
    [Form.name]: Form,
    [NavBar.name]: NavBar,
  },
  data() {
    return {
      userInfo: {},
      userInfoReady: false,
      loadingUserInfo: false,
      loading: false,
      formData: {
        oldpass: "",
        newpass: "",
        confirmpass: "",
      },
    };
  },
  computed: {
    requiresOldPassword() {
      return this.userInfo.setpaypass === 1 || this.userInfo.setpaypass === "1";
    },
    busy() {
      return this.loading || this.loadingUserInfo;
    },
    formRules() {
      return {
        oldpass: [
          {
            required: true,
            validator: this.isNotBlank,
            message: this.$t("password.old.placeholder"),
          },
        ],
        newpass: [
          {
            required: true,
            validator: this.isNotBlank,
            message: this.$t("password.new.placeholder"),
          },
        ],
        confirmpass: [
          {
            required: true,
            validator: this.isNotBlank,
            message: this.$t("password.confirmPassword.placeholder"),
          },
          {
            validator: (value) => value === this.formData.newpass,
            message: this.$t("hint.passwordDiff"),
          },
        ],
      };
    },
  },
  created() {
    this.loadUserInfo();
  },
  methods: {
    onClickLeft() {
      this.$router.go(-1);
    },
    isNotBlank(value) {
      return typeof value === "string" && value.trim().length > 0;
    },
    getErrorMessage(error) {
      if (typeof error === "string") return error;
      return (
        (error && error.response && error.response.data && error.response.data.msg) ||
        (error && error.msg) ||
        this.$t("Hint.failed")
      );
    },
    async loadUserInfo() {
      this.loadingUserInfo = true;
      try {
        const response = await userinfo();
        const { ret, msg, data } = response.data;
        if (Number(ret) !== 1) {
          Toast(msg || this.$t("Hint.failed"));
          return false;
        }
        this.userInfo = data || {};
        this.$store.commit("SET_USERINFO", this.userInfo);
        this.userInfoReady = true;
        return true;
      } catch (error) {
        Toast(this.getErrorMessage(error));
        return false;
      } finally {
        this.loadingUserInfo = false;
      }
    },
    async submit() {
      if (this.busy) return;

      return this.$submitOnce("payment-password", async () => {
        if (!this.userInfoReady) {
          if (!(await this.loadUserInfo())) return;
          await this.$nextTick();
        }

        try {
          await this.$refs.passwordForm.validate();
        } catch (errors) {
          return;
        }

        this.loading = true;
        try {
          const payload = {
            newpass: this.formData.newpass,
          };
          if (this.requiresOldPassword) {
            payload.oldpass = this.formData.oldpass;
          }

          const response = await SETTING_PAY_PDW(payload);
          const { ret, msg } = response.data;
          if (Number(ret) !== 1) {
            Toast(msg || this.$t("Hint.failed"));
            return;
          }

          this.$store.commit("SET_USERINFO", { setpaypass: 1 });
          Toast(msg || this.$t("Hint.success"));
          this.$router.go(-1);
        } catch (error) {
          Toast(this.getErrorMessage(error));
        } finally {
          this.loading = false;
        }
      });
    },
  },
};
</script>

<style lang="less" scoped>
#PaymentCode {
  width: 100%;
  min-height: 100vh;
  background: var(--page-background);

  .payment-password-content {
    padding: calc(16rem / 16) calc(16rem / 16) calc(32rem / 16);
  }

  .password-fields {
    overflow: hidden;
    border-radius: calc(10rem / 16);
    background: #fff;
  }

  /deep/ .van-field {
    display: block;
    padding: calc(15rem / 16) calc(16rem / 16);
  }

  /deep/ .van-field__label {
    width: auto;
    margin: 0 0 calc(8rem / 16);
    color: #333;
    font-size: calc(14rem / 16);
    line-height: 1.5;
  }

  /deep/ .van-field__value {
    width: 100%;
  }

  /deep/ .van-field__control {
    font-size: calc(14rem / 16);
    line-height: 1.6;
  }

  /deep/ .van-field__error-message {
    margin-top: calc(5rem / 16);
    line-height: 1.5;
  }

  .button-confirm {
    margin-top: calc(24rem / 16);
  }

  .payment-password-submit {
    height: auto;
    padding: calc(13rem / 16) calc(16rem / 16);
    border: 0;
    border-radius: calc(8rem / 16);
    background: var(--button-background);
    color: var(--button-text);
    font-size: calc(16rem / 16);
    line-height: 1.5;
  }
}
</style>
